import { Provider } from '@angular/core';
import { DesignerPageComponentType } from '../../models';
import { SPACER_COMPONENT_FORM_SCHEMA } from '../../schemas/components/page';
import { PAGE_COMPONENTS_REGISTRY, PageComponentDefinition } from './page.components.service';

/**
 * Default page-component dictionary. Intentionally NOT re-exported through the services
 * barrel (`libs/shared/src/services/components-helpers/index.ts`) nor through the main
 * `libs/shared/src` barrel — apps must import the provider from this file by deep path
 * so the template references stay out of the services-barrel transitive dependency graph.
 *
 * This is the slim *render* registry: it carries no `formSchema` entries (the Designer-only
 * settings forms live in `page.components.schemas.ts`) so the schema definitions tree-shake
 * out of the public CloudFront bundle. The Designer wires the schemas back on via
 * `PAGE_COMPONENTS_WITH_SCHEMAS_PROVIDER`. The single exception is `Spacer`, whose schema
 * defaults the public renderer reads (`getComponentDefaultSettings('Spacer')`), so it is kept here.
 *
 * The entry order defines the order of the components within their group in the Designer sidemenu.
 */
export const PAGE_COMPONENTS_DEFAULT: Record<DesignerPageComponentType, PageComponentDefinition> = {
  // Basic
  Text: {
    name: 'components.text.name',
    group: 'basic',
    iconName: 'text-t',
    loadComponent: () => import('../../template/app/components/text/text.component').then((m) => m.TextComponent),
    backendComponent: false,
    bcModules: ['*', 'website', 'webshop']
  },
  Image: {
    name: 'components.image.name',
    group: 'basic',
    iconName: 'image',
    loadComponent: () => import('../../template/app/components/image/image.component').then((m) => m.ImageComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  Html: {
    name: 'components.html.name',
    group: 'basic',
    iconName: 'code',
    loadComponent: () => import('../../template/app/components/html/html.component').then((m) => m.HtmlComponent),
    backendComponent: false,
    bcModules: ['*', 'website', 'webshop']
  },
  IFrame: {
    name: 'components.iFrame.name',
    group: 'basic',
    iconName: 'code-block',
    loadComponent: () => import('../../template/app/components/iframe/iframe.component').then((m) => m.IFrameComponent),
    backendComponent: false,
    bcModules: ['*', 'website', 'webshop']
  },
  Button: {
    name: 'components.button.name',
    group: 'basic',
    iconName: 'rectangle',
    iconWeight: 'regular',
    loadComponent: () => import('../../template/app/components/button/button.component').then((m) => m.ButtonComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  Spacer: {
    name: 'components.spacer.name',
    group: 'basic',
    iconName: 'arrows-vertical',
    loadComponent: () => import('../../template/app/components/spacer/spacer.component').then((m) => m.SpacerComponent),
    formSchema: SPACER_COMPONENT_FORM_SCHEMA,
    backendComponent: false,
    bcModules: ['*', 'website', 'webshop']
  },

  // Content: images & media
  Gallery: {
    name: 'components.gallery.name',
    group: 'content',
    iconName: 'images',
    loadComponent: () => import('../../template/app/components/gallery/gallery.component').then((m) => m.GalleryComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  ContainerGallery: {
    name: 'components.containerGallery.name',
    group: 'content',
    iconName: 'folders',
    loadComponent: () =>
      import('../../template/app/components/container-gallery/container-gallery.component').then((m) => m.ContainerGalleryComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  SearchGallery: {
    name: 'components.searchGallery.name',
    group: 'content',
    iconName: 'folder-open',
    loadComponent: () =>
      import('../../template/app/components/search-gallery/search-gallery.component').then((m) => m.SearchGalleryComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  VideoDetail: {
    name: 'components.videoDetail.name',
    group: 'content',
    iconName: 'play',
    loadComponent: () => import('../../template/app/components/video-detail/video-detail.component').then((m) => m.VideoDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  // Content: company & locations
  CompanyInfo: {
    name: 'components.companyInfo.name',
    group: 'content',
    iconName: 'map-pin',
    loadComponent: () => import('../../template/app/components/company-info/company-info.component').then((m) => m.CompanyInfoComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  ChainstoreList: {
    name: 'components.chainstoreList.name',
    group: 'content',
    iconName: 'map-trifold',
    loadComponent: () =>
      import('../../template/app/components/chainstore-list/chainstore-list.component').then((m) => m.ChainstoreListComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  ChainstorePlaces: {
    name: 'components.chainstorePlaces.name',
    group: 'content',
    iconName: 'map-pin',
    loadComponent: () =>
      import('../../template/app/components/chainstore-places/chainstore-places.component').then((m) => m.ChainstorePlacesComponent),
    backendComponent: true,
    bcModules: ['*', 'local-seo']
  },
  Partner: {
    name: 'components.partner.name',
    group: 'content',
    iconName: 'users',
    loadComponent: () => import('../../template/app/components/partner/partner.component').then((m) => m.PartnerComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  VacancyList: {
    name: 'components.vacancyList.name',
    group: 'content',
    iconName: 'clipboard-text',
    loadComponent: () => import('../../template/app/components/vacancy-list/vacancy-list.component').then((m) => m.VacancyListComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  // Content: dates & events
  Calendar: {
    name: 'components.calendar.name',
    group: 'content',
    iconName: 'calendar-dots',
    loadComponent: () => import('../../template/app/components/calendar/calendar.component').then((m) => m.CalendarComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  Timeline: {
    name: 'components.timeline.name',
    group: 'content',
    iconName: 'calendar-blank',
    loadComponent: () => import('../../template/app/components/timeline/timeline.component').then((m) => m.TimelineComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  Appointment: {
    name: 'components.appointment.name',
    group: 'content',
    iconName: 'calendar-check',
    iconWeight: 'regular',
    loadComponent: () => import('../../template/app/components/appointment/appointment.component').then((m) => m.AppointmentComponent),
    backendComponent: true,
    bcModules: ['*', 'website']
  },
  Reservation: {
    name: 'components.reservation.name',
    group: 'content',
    iconName: 'fork-knife',
    iconWeight: 'regular',
    loadComponent: () => import('../../template/app/components/reservation/reservation.component').then((m) => m.ReservationComponent),
    backendComponent: true,
    bcModules: ['*', 'website']
  },
  // Content: contact & interaction
  ContactForm: {
    name: 'components.contactForm.name',
    group: 'content',
    iconName: 'envelope',
    iconWeight: 'regular',
    loadComponent: () => import('../../template/app/components/contact-form/contact-form.component').then((m) => m.ContactFormComponent),
    backendComponent: true,
    bcModules: ['*', 'website']
  },
  Newsletter: {
    name: 'components.newsletter.name',
    group: 'content',
    iconName: 'envelope-open',
    iconWeight: 'regular',
    loadComponent: () => import('../../template/app/components/newsletter/newsletter.component').then((m) => m.NewsletterComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  FAQs: {
    name: 'components.faqs.name',
    group: 'content',
    iconName: 'question',
    iconWeight: 'fill',
    loadComponent: () => import('../../template/app/components/faq/faq.component').then((m) => m.FaqComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  PlantDoctor: {
    name: 'components.plantDoctor.name',
    group: 'content',
    iconName: 'stethoscope',
    iconWeight: 'light',
    loadComponent: () => import('../../template/app/components/plant-doctor/plant-doctor.component').then((m) => m.PlantDoctorComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  PageSteps: {
    name: 'components.pageSteps.name',
    group: 'content',
    iconName: 'path',
    iconWeight: 'light',
    loadComponent: () => import('../../template/app/components/page-steps/page-steps.component').then((m) => m.PageStepsComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  // Content: ratings & products
  Ratings: {
    name: 'components.ratings.name',
    group: 'content',
    iconName: 'star',
    loadComponent: () => import('../../template/app/components/ratings/ratings.component').then((m) => m.RatingsComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  ArticleRatings: {
    name: 'components.articleRating.name',
    group: 'content',
    iconName: 'star',
    loadComponent: () =>
      import('../../template/app/components/article-rating/article-ratings.component').then((m) => m.ArticleRatingsComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  CrossEntityTabs: {
    name: 'components.crossEntityTabs.name',
    group: 'content',
    iconName: 'link',
    loadComponent: () =>
      import('../../template/app/components/cross-entity-tabs/cross-entity-tabs.component').then((m) => m.CrossEntityTabsComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  // Content: other
  Elfsight: {
    name: 'components.elfsight.name',
    group: 'content',
    iconName: 'code-block',
    loadComponent: () => import('../../template/app/components/elfsight/elfsight.component').then((m) => m.ElfsightComponent),
    backendComponent: true,
    bcModules: ['*', 'social']
  },
  CustomCode: {
    name: 'components.customCode.name',
    group: 'content',
    iconName: 'code',
    loadComponent: () => import('../../template/app/components/custom-code/custom-code.component').then((m) => m.CustomCodeComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop'],
    adminOnly: true
  },

  // Shop & order
  ShoppingCart: {
    name: 'components.shoppingCart.name',
    group: 'shop',
    iconName: 'basket',
    loadComponent: () =>
      import('../../template/app/components/shopping-cart/shopping-cart-overview/shopping-cart-overview.component').then(
        (m) => m.ShoppingCartOverviewComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  // Request list (CS4-1335): delegation wrappers that can be overridden per template.config.json.
  // They forward to the base component, the behaviour differs only in the backend endpoint that
  // `componentType` resolves to.
  InquiryShoppingCart: {
    name: 'components.inquiryShoppingCart.name',
    group: 'shop',
    iconName: 'list-checks',
    loadComponent: () =>
      import('../../template/app/components/inquiry-shopping-cart/inquiry-shopping-cart.component').then(
        (m) => m.InquiryShoppingCartComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'b2c']
  },
  Checkout: {
    name: 'components.checkout.name',
    group: 'shop',
    iconName: 'cash-register',
    loadComponent: () => import('../../template/app/components/checkout/checkout.component').then((m) => m.CheckoutComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  InquiryCheckout: {
    name: 'components.inquiryCheckout.name',
    group: 'shop',
    iconName: 'receipt',
    loadComponent: () =>
      import('../../template/app/components/inquiry-checkout/inquiry-checkout.component').then((m) => m.InquiryCheckoutComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'b2c']
  },
  CheckoutGuestGateway: {
    name: 'components.checkoutGuestGateway.name',
    group: 'shop',
    iconName: 'question',
    loadComponent: () =>
      import('../../template/app/components/checkout-guest-gateway/checkout-guest-gateway.component').then(
        (m) => m.CheckoutGuestGatewayComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  InquiryCheckoutGuestGateway: {
    name: 'components.inquiryCheckoutGuestGateway.name',
    group: 'shop',
    iconName: 'user-list',
    loadComponent: () =>
      import('../../template/app/components/inquiry-checkout-guest-gateway/inquiry-checkout-guest-gateway.component').then(
        (m) => m.InquiryCheckoutGuestGatewayComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'b2c']
  },
  OrderReady: {
    name: 'components.orderReady.name',
    group: 'shop',
    iconName: 'file-text',
    loadComponent: () => import('../../template/app/components/order-ready/order-ready.component').then((m) => m.OrderReadyComponent),
    backendComponent: false,
    bcModules: ['*', 'webshop', 'voucher']
  },
  ShippingCalculator: {
    name: 'components.shippingCalculator.name',
    group: 'shop',
    iconName: 'calculator',
    loadComponent: () =>
      import('../../template/app/components/shipping-calculator/shipping-calculator.component').then((m) => m.ShippingCalculatorComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  Search: {
    name: 'components.search.name',
    group: 'shop',
    iconName: 'magnifying-glass',
    loadComponent: () => import('../../template/app/components/search/search.component').then((m) => m.SearchComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  OrderRevocationForm: {
    name: 'components.orderRevocationForm.name',
    group: 'shop',
    iconName: 'file-text',
    loadComponent: () =>
      import('../../template/app/components/order-revocation-form/order-revocation-form.component').then(
        (m) => m.OrderRevocationFormComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  ShareLinkUnavailable: {
    name: 'components.shareLinkUnavailable.name',
    group: 'shop',
    iconName: 'link-break',
    loadComponent: () =>
      import('../../template/app/components/share-link-unavailable/share-link-unavailable.component').then(
        (m) => m.ShareLinkUnavailableComponent
      ),
    backendComponent: false,
    bcModules: ['dam']
  },

  // Account: sign-in
  Login: {
    name: 'components.login.name',
    group: 'account',
    iconName: 'sign-in',
    loadComponent: () => import('../../template/app/components/login/login.component').then((m) => m.LoginComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  Logout: {
    name: 'components.logout.name',
    group: 'account',
    iconName: 'sign-out',
    loadComponent: () => import('../../template/app/components/logout/logout.component').then((m) => m.LogoutComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  Register: {
    name: 'components.register.name',
    group: 'account',
    iconName: 'user-plus',
    loadComponent: () => import('../../template/app/components/register/register.component').then((m) => m.RegisterComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  RegistrationConfirm: {
    name: 'components.registrationConfirm.name',
    group: 'account',
    iconName: 'user-check',
    loadComponent: () =>
      import('../../template/app/components/registration-confirm/registration-confirm.component').then(
        (m) => m.RegistrationConfirmComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  ForgotUserData: {
    name: 'components.forgotUserData.name',
    group: 'account',
    iconName: 'lock',
    loadComponent: () =>
      import('../../template/app/components/forgot-user-data/forgot-user-data.component').then((m) => m.ForgotUserDataComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  ChangePassword: {
    name: 'components.changePassword.name',
    group: 'account',
    iconName: 'key',
    loadComponent: () =>
      import('../../template/app/components/change-password/change-password.component').then((m) => m.ChangePasswordComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  // Account: my account
  Dashboard: {
    name: 'components.dashboard.name',
    group: 'account',
    iconName: 'identification-card',
    loadComponent: () => import('../../template/app/components/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  MemberChangeProfile: {
    name: 'components.memberChangeProfile.name',
    group: 'account',
    iconName: 'user-gear',
    loadComponent: () =>
      import('../../template/app/components/member-change-profile/member-change-profile.component').then(
        (m) => m.MemberChangeProfileComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  MemberRemoveProfile: {
    name: 'components.memberRemoveProfile.name',
    group: 'account',
    iconName: 'user-minus',
    loadComponent: () =>
      import('../../template/app/components/member-remove-profile/member-remove-profile.component').then(
        (m) => m.MemberRemoveProfileComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  Orders: {
    name: 'components.orders.name',
    group: 'account',
    iconName: 'file-text',
    loadComponent: () => import('../../template/app/components/orders/orders.component').then((m) => m.OrdersComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  Watchlist: {
    name: 'components.watchlist.name',
    group: 'account',
    iconName: 'heart',
    loadComponent: () => import('../../template/app/components/watchlist/watchlist.component').then((m) => m.WatchlistComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  Document: {
    name: 'components.document.name',
    group: 'account',
    iconName: 'certificate',
    loadComponent: () =>
      import('../../template/app/components/legal-documents/legal-documents.component').then((m) => m.LegalDocumentsComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  NewsletterConfirm: {
    name: 'components.newsletterConfirm.name',
    group: 'account',
    iconName: 'envelope-simple',
    iconWeight: 'regular',
    loadComponent: () =>
      import('../../template/app/components/newsletter-confirm/newsletter-confirm.component').then((m) => m.NewsletterConfirmComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  // Account: debit card & loyalty club
  DebitCardDetail: {
    name: 'components.debitCardDetail.name',
    group: 'account',
    iconName: 'credit-card',
    iconWeight: 'regular',
    loadComponent: () =>
      import('../../template/app/components/debit-card-detail/debit-card-detail.component').then((m) => m.DebitCardDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'loyalty', 'loyalty-web']
  },
  HasNoDebitCard: {
    name: 'components.hasNoDebitCard.name',
    group: 'account',
    iconName: 'identification-card',
    loadComponent: () =>
      import('../../template/app/components/has-no-debit-card/has-no-debit-card.component').then((m) => m.HasNoDebitCardComponent),
    backendComponent: true,
    bcModules: ['*', 'loyalty', 'loyalty-web']
  },
  DebitCardLoyaltyStatus: {
    name: 'components.debitCardLoyaltyStatus.name',
    group: 'account',
    iconName: 'medal',
    iconWeight: 'regular',
    loadComponent: () =>
      import('../../template/app/components/debit-card-loyalty-status/debit-card-loyalty-status.component').then(
        (m) => m.DebitCardLoyaltyStatusComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'loyalty', 'loyalty-web']
  },
  DebitCardLoyaltyStatusHistory: {
    name: 'components.debitCardLoyaltyStatusHistory.name',
    group: 'account',
    iconName: 'clock-counter-clockwise',
    iconWeight: 'regular',
    loadComponent: () =>
      import('../../template/app/components/debit-card-loyalty-status-history/debit-card-loyalty-status-history.component').then(
        (m) => m.DebitCardLoyaltyStatusHistoryComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'loyalty', 'loyalty-web']
  },
  DebitCardVouchers: {
    name: 'components.debitCardVouchers.name',
    group: 'account',
    iconName: 'ticket',
    iconWeight: 'regular',
    loadComponent: () =>
      import('../../template/app/components/debit-card-vouchers/debit-card-vouchers.component').then((m) => m.DebitCardVouchersComponent),
    backendComponent: true,
    bcModules: ['*', 'loyalty', 'loyalty-web']
  },
  Promotions: {
    name: 'components.promotions.name',
    group: 'account',
    iconName: 'gift',
    iconWeight: 'regular',
    loadComponent: () => import('../../template/app/components/promotions/promotions.component').then((m) => m.PromotionsComponent),
    backendComponent: true,
    bcModules: ['*', 'loyalty', 'loyalty-web']
  },

  // Detail
  ArticleInfo: {
    name: 'components.articleInfo.name',
    group: 'detail',
    iconName: 'flower',
    loadComponent: () =>
      import('../../template/app/components/article-infos/article-info/article-info.component').then((m) => m.ArticleInfoComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop']
  },
  ArticleInfoTicket: {
    name: 'components.articleInfoTicket.name',
    group: 'detail',
    iconName: 'ticket',
    loadComponent: () =>
      import('../../template/app/components/article-infos/article-info-ticket/article-info-ticket.component').then(
        (m) => m.ArticleInfoTicketComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'webshop']
  },
  ArticleInfoVoucher: {
    name: 'components.articleInfoVoucher.name',
    group: 'detail',
    iconName: 'gift',
    loadComponent: () =>
      import('../../template/app/components/article-infos/article-info-voucher/article-info-voucher.component').then(
        (m) => m.ArticleInfoVoucherComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  ArticleInfoCemetery: {
    name: 'components.articleInfoCemetery.name',
    group: 'detail',
    iconName: 'cross',
    loadComponent: () =>
      import('../../template/app/components/article-infos/article-info-cemetery/article-info-cemetery.component').then(
        (m) => m.ArticleInfoCemeteryComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'webshop']
  },
  ArticleInfoRollLawn: {
    name: 'components.articleInfoRollLawn.name',
    group: 'detail',
    iconName: 'ruler',
    loadComponent: () =>
      import('../../template/app/components/article-infos/article-info-rolllawn/article-info-rolllawn.component').then(
        (m) => m.ArticleInfoRollLawnComponent
      ),
    backendComponent: true,
    bcModules: ['*', 'webshop']
  },
  OrderDetail: {
    name: 'components.orderDetail.name',
    group: 'detail',
    iconName: 'file-text',
    loadComponent: () => import('../../template/app/components/order-detail/order-detail.component').then((m) => m.OrderDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'webshop', 'voucher']
  },
  ReportDetail: {
    name: 'components.reportDetail.name',
    group: 'detail',
    iconName: 'file-text',
    loadComponent: () => import('../../template/app/components/report-detail/report-detail.component').then((m) => m.ReportDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'plant-doctor']
  },
  NewsDetail: {
    name: 'components.newsDetail.name',
    group: 'detail',
    iconName: 'newspaper',
    loadComponent: () => import('../../template/app/components/news-detail/news-detail.component').then((m) => m.NewsDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  LeafletDetail: {
    name: 'components.leafletDetail.name',
    group: 'detail',
    iconName: 'book-open',
    loadComponent: () =>
      import('../../template/app/components/leaflet-detail/leaflet-detail.component').then((m) => m.LeafletDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  CalendarDetail: {
    name: 'components.calendarDetail.name',
    group: 'detail',
    iconName: 'calendar-dots',
    loadComponent: () =>
      import('../../template/app/components/calendar-detail/calendar-detail.component').then((m) => m.CalendarDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  VacancyDetail: {
    name: 'components.vacancyDetail.name',
    group: 'detail',
    iconName: 'briefcase',
    loadComponent: () =>
      import('../../template/app/components/vacancy-detail/vacancy-detail.component').then((m) => m.VacancyDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  },
  TeamMemberDetail: {
    name: 'components.teamMemberDetail.name',
    group: 'detail',
    iconName: 'images',
    loadComponent: () =>
      import('../../template/app/components/team-member-detail/team-member-detail.component').then((m) => m.TeamMemberDetailComponent),
    backendComponent: false,
    bcModules: ['*', 'website', 'webshop']
  },
  ChainstoreDetail: {
    name: 'components.chainstoreDetail.name',
    group: 'detail',
    iconName: 'map-pin',
    loadComponent: () =>
      import('../../template/app/components/chainstore-detail/chainstore-detail.component').then((m) => m.ChainstoreDetailComponent),
    backendComponent: true,
    bcModules: ['*', 'website', 'webshop']
  }
};

/** Provider for the default page-component registry. Register once in app.config to wire it up. */
export const PAGE_COMPONENTS_REGISTRY_PROVIDER: Provider = {
  provide: PAGE_COMPONENTS_REGISTRY,
  useValue: PAGE_COMPONENTS_DEFAULT
};
